import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import routes from 'routes';
import MetaTags from 'components/common/MetaTags';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import CreatableClubSelect, {
  UNATTACHED,
} from 'components/admin/MeetRegistration/AddEditRegistrationPopup/CreatableClubSelect';
import {
  getMyMeetRegistrations,
  selfRegisterAthlete,
  getAthleteAvailableEvents,
} from 'api/meetRegistrationApi';
import { createMyAthlete } from 'api/userApi';

import styles from './MeetOnboardingWizard.module.css';

// Case 3 (§6.6) — the GUIDED onboarding wizard, ONLY for a brand-new person who just created their account via the
// dialog and has no athletes yet. A linear, can't-get-lost funnel: add an athlete (name + DOB + gender + their club +
// bib) → choose events → registered. It reuses the SAME building blocks (createMyAthlete, athlete-events, selfRegister,
// CreatableClubSelect) — a different presentation, not a different system. The athlete is created only when they hit
// Register, so going Back is safe. Each Register mints the athlete as a host-club Visitor; Finish drops them into their
// personal My Registrations (Case 2). A persistent meet header keeps the meet/club context visible the whole way.

const fmtWallClock = (s) => (s ? s.replace('T', ' ').slice(0, 16) : '');

const eventsHint = (ageGroup, gender, maxEvents) => {
  const ctx = [ageGroup != null ? String(ageGroup) : '', gender].filter(Boolean).join(' ');
  if (maxEvents != null) {
    return `${ctx ? `${ctx} athletes` : 'These athletes'} can enter up to ${maxEvents} event${
      maxEvents === 1 ? '' : 's'
    }.`;
  }
  return ctx ? `Events for ${ctx} athletes.` : 'Available events.';
};

const blankAthlete = {
  firstName: '',
  lastName: '',
  birthDate: '',
  gender: '',
  externalClub: '', // '' = nothing chosen yet → must pick a club or "No club / unattached" before continuing
  bib: '',
  noBib: false,
};
const blankEvents = { list: [], selected: [], maxEvents: null, ageGroup: null, loading: false };

const MeetOnboardingWizard = () => {
  const { meetId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [meet, setMeet] = useState(null); // { meetName, meetDate, location, participatingClubs, hostClubName }
  const [step, setStep] = useState('welcome'); // welcome | addAthlete | events | done

  const [form, setForm] = useState(blankAthlete); // the athlete being added (not created until Register)
  const [events, setEvents] = useState(blankEvents); // the events step
  const [busy, setBusy] = useState(false);
  const [registered, setRegistered] = useState([]); // [{ name, eventCount }]

  const loadMeet = useCallback(async () => {
    try {
      const res = await getMyMeetRegistrations(meetId);
      setMeet(res.data);
    } catch (err) {
      toast.error(err.response?.data || 'This signup isn’t available.');
    } finally {
      setLoading(false);
    }
  }, [meetId]);

  useEffect(() => {
    loadMeet();
  }, [loadMeet]);

  const hostClubName = meet?.hostClubName || '';
  const clubOptions = (() => {
    const host = hostClubName.trim().toLowerCase();
    const byKey = new Map();
    (meet?.participatingClubs || []).forEach((c) => {
      const n = (c.clubName || '').trim();
      if (!n) return;
      const k = n.toLowerCase();
      if (k === host || byKey.has(k)) return;
      byKey.set(k, n);
    });
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
  })();

  // Step 1 → 2: validate the athlete + bib, then load the events eligible for their age group / gender. The athlete is
  // NOT created yet (that happens on Register), so the user can go Back and edit without creating duplicates.
  const goToEvents = async () => {
    const { firstName, lastName, birthDate, gender, externalClub, bib, noBib } = form;
    if (!firstName.trim() || !lastName.trim() || !birthDate || !gender) {
      toast.error('Enter the athlete’s name, date of birth and gender');
      return;
    }
    if (!externalClub) {
      toast.error('Choose the club they represent, or select “No club / unattached”');
      return;
    }
    if (!noBib && !(Number(bib) > 0)) {
      toast.error('Enter a bib number, or choose “No number — the club will assign one”');
      return;
    }
    setBusy(true);
    setEvents({ ...blankEvents, loading: true });
    setStep('events');
    try {
      const ev = await getAthleteAvailableEvents(meetId, { birthDate, gender });
      setEvents({
        list: ev.data.events || [],
        selected: [],
        maxEvents: ev.data.maxEvents ?? null,
        ageGroup: ev.data.ageGroup ?? null,
        loading: false,
      });
    } catch (err) {
      toast.error(err.response?.data || 'Failed to load events');
      setEvents((prev) => ({ ...prev, loading: false }));
    } finally {
      setBusy(false);
    }
  };

  const toggleEvent = (code) => {
    setEvents((prev) => {
      const has = prev.selected.includes(code);
      if (!has && prev.maxEvents != null && prev.selected.length >= prev.maxEvents) return prev;
      return {
        ...prev,
        selected: has ? prev.selected.filter((c) => c !== code) : [...prev.selected, code],
      };
    });
  };

  // Step 2 → done: create the athlete on the account, then register them (which mints them as a Visitor).
  const submitRegistration = async () => {
    if (events.selected.length === 0) {
      toast.error('Choose at least one event');
      return;
    }
    setBusy(true);
    try {
      const created = await createMyAthlete({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        birthDate: form.birthDate,
        gender: form.gender,
      });
      const athleteProfileId = created.data?.athleteProfileId || created.data?.AthleteProfileId;
      await selfRegisterAthlete(meetId, {
        athleteProfileId,
        eventCodes: events.selected,
        noBib: form.noBib,
        bib: form.noBib ? null : Number(form.bib),
        updateRepresentingClub: true,
        externalClubName: form.externalClub === UNATTACHED ? null : form.externalClub.trim() || null,
      });
      setRegistered((prev) => [
        ...prev,
        { name: `${form.firstName.trim()} ${form.lastName.trim()}`, eventCount: events.selected.length },
      ]);
      setForm(blankAthlete);
      setEvents(blankEvents);
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data || 'Failed to register. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const startAnother = () => {
    setForm(blankAthlete);
    setEvents(blankEvents);
    setStep('addAthlete');
  };

  const finish = () => navigate(routes.personal.meetRegistrations.url(meetId));

  if (loading) return <div className={styles.page}><div className={styles.loading}>Loading…</div></div>;

  return (
    <div className={styles.page}>
      <MetaTags title="SplitFast | Register for a Meet" />

      {/* Persistent meet context — so they always know what / whose meet they're registering for. */}
      <div className={styles.meetHeader}>
        {hostClubName && <div className={styles.meetHost}>{hostClubName}</div>}
        <div className={styles.meetName}>{meet?.meetName || 'Meet registration'}</div>
        {(meet?.meetDate || meet?.location) && (
          <div className={styles.meetMeta}>
            {meet?.meetDate ? fmtWallClock(meet.meetDate) : ''}
            {meet?.location ? `${meet?.meetDate ? ' · ' : ''}${meet.location}` : ''}
          </div>
        )}
      </div>

      <div className={styles.card}>
        {step === 'welcome' && (
          <>
            <div className={styles.kicker}>You’re all set up 🎉</div>
            <h1 className={styles.title}>Let’s register your athletes</h1>
            <p className={styles.lead}>
              Add an athlete, choose their events, and you’re done — you can add as many as you like.
            </p>
            <div className={styles.actions}>
              <CustomNewButton variant="primary2" onClick={() => setStep('addAthlete')}>
                Add an athlete
              </CustomNewButton>
            </div>
          </>
        )}

        {step === 'addAthlete' && (
          <>
            <div className={styles.kicker}>Step 1 — the athlete</div>
            <h1 className={styles.title}>Who are you registering?</h1>
            <div className={styles.fieldRow}>
              <div className={styles.fieldHalf}>
                <label className={styles.label}>First name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={form.firstName}
                  onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
                />
              </div>
              <div className={styles.fieldHalf}>
                <label className={styles.label}>Last name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={form.lastName}
                  onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
                />
              </div>
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.fieldHalf}>
                <label className={styles.label}>Date of birth</label>
                <input
                  type="date"
                  className={`${styles.input} ${styles.dateInput} ${form.birthDate ? styles.hasDate : ''}`}
                  value={form.birthDate}
                  onChange={(e) => setForm((p) => ({ ...p, birthDate: e.target.value }))}
                />
              </div>
              <div className={styles.fieldHalf}>
                <label className={styles.label}>Gender</label>
                <select
                  className={styles.input}
                  value={form.gender}
                  onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}
                >
                  <option value="">Select…</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.fieldHalf}>
                <label className={styles.label}>Club they represent</label>
                <CreatableClubSelect
                  clubs={clubOptions}
                  value={form.externalClub}
                  onChange={(v) => setForm((p) => ({ ...p, externalClub: v }))}
                  unattachedLabel="No club / unattached"
                  placeholder="Type your club…"
                />
              </div>
              <div className={styles.fieldHalf}>
                <label className={styles.label}>Bib number</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className={styles.bibInput}
                  value={form.bib}
                  disabled={form.noBib}
                  onChange={(e) => setForm((p) => ({ ...p, bib: e.target.value.replace(/[^0-9]/g, '') }))}
                />
                <label className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={form.noBib}
                    onChange={(e) => setForm((p) => ({ ...p, noBib: e.target.checked }))}
                  />
                  No number — the club will assign one
                </label>
              </div>
            </div>
            <div className={styles.actions}>
              {registered.length > 0 && (
                <CustomNewButton variant="outline" onClick={() => setStep('done')} disabled={busy}>
                  Back
                </CustomNewButton>
              )}
              <CustomNewButton variant="primary2" onClick={goToEvents} disabled={busy}>
                {busy ? 'Loading…' : 'Next: choose events'}
              </CustomNewButton>
            </div>
          </>
        )}

        {step === 'events' && (
          <>
            <div className={styles.kicker}>
              Step 2 — events for {form.firstName} {form.lastName}
            </div>
            <h1 className={styles.title}>Choose their events</h1>

            <div className={styles.eventsHeader}>
              <span className={styles.label}>Events</span>
              <span className={styles.countHint}>
                {events.selected.length}
                {events.maxEvents != null ? ` / ${events.maxEvents}` : ''} selected
              </span>
            </div>
            {!events.loading && (events.ageGroup != null || events.maxEvents != null) && (
              <p className={styles.eventsNote}>{eventsHint(events.ageGroup, form.gender, events.maxEvents)}</p>
            )}
            {events.loading ? (
              <div className={styles.empty}>Loading events…</div>
            ) : events.list.length === 0 ? (
              <div className={styles.empty}>
                This meet’s program doesn’t include events for this athlete. If you think that’s wrong,
                please contact a club official.
              </div>
            ) : (
              <div className={styles.eventsGrid}>
                {events.list.map((e) => {
                  const checked = events.selected.includes(e.eventCode);
                  const capReached =
                    events.maxEvents != null && events.selected.length >= events.maxEvents;
                  return (
                    <label key={e.eventCode} className={styles.eventOption}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!checked && capReached}
                        onChange={() => toggleEvent(e.eventCode)}
                      />
                      {e.displayName}
                    </label>
                  );
                })}
              </div>
            )}

            <div className={styles.actions}>
              <CustomNewButton variant="outline" onClick={() => setStep('addAthlete')} disabled={busy}>
                Back
              </CustomNewButton>
              <CustomNewButton variant="success" onClick={submitRegistration} disabled={busy || events.loading}>
                {busy ? 'Registering…' : 'Register'}
              </CustomNewButton>
            </div>
          </>
        )}

        {step === 'done' && (
          <>
            <div className={styles.confirmIcon}>✓</div>
            <h1 className={styles.title}>
              {registered.length === 1 ? 'Registered!' : `${registered.length} athletes registered!`}
            </h1>
            <ul className={styles.summaryList}>
              {registered.map((r, i) => (
                <li key={i}>
                  <strong>{r.name}</strong> — {r.eventCount} event{r.eventCount === 1 ? '' : 's'}
                </li>
              ))}
            </ul>
            <p className={styles.lead}>
              You can add another athlete, or finish and manage everything from your registrations page.
            </p>
            <div className={styles.actions}>
              <CustomNewButton variant="outline" onClick={startAnother}>
                Add another athlete
              </CustomNewButton>
              <CustomNewButton variant="primary2" onClick={finish}>
                Finish
              </CustomNewButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MeetOnboardingWizard;
